import { useId } from 'react';
import { HandMark } from '../HandMark';
import { photoHoldRole } from '../photo-hold-roles';
import { holdClipPath, holdPath, holdStrokeWidth } from '../hold-outline';
import { HOLD_HANDLES, type PhotoHoldsController } from './usePhotoHolds';
import { ZoomResetButton } from './ZoomResetButton';
import type { BoardHand } from '../../../types/device';
import type { Hold } from '../../../types/climb';

/** How thick a circle's ring is drawn — `border-[3px]` on the button below. */
const PHOTO_RING_PX = 3;

/**
 * How far the lights go down over the wall, and how far up over the holds that
 * are in. Dark enough that a lit hold is unmistakable across a room, light
 * enough that the wall is still a wall and a placeholder on it can be found.
 */
const SPOTLIGHT_DIM = 0.42;
const SPOTLIGHT_LIT = 1.08;

/**
 * How a maybe is told from a placeholder: the same white, fainter, and dotted
 * rather than dashed. **Not a colour** — boardsesh draws theirs amber, but
 * here orange is a foot and yellow a no-match (photo-hold-roles), and a maybe
 * must not read as a role it has not been given.
 */
const MAYBE_OPACITY = 0.35;

interface PhotoHoldCanvasProps {
  editor: PhotoHoldsController;
  imageUrl: string;
  /**
   * How a circle is named to a screen reader, given its role and where it sits.
   * The page supplies it: the same editor circles a scan's holds in the gym
   * panel and a boulder's on the member app, and each names them its own way.
   */
  holdLabel: (hold: { role: string; x: number; y: number }) => string;
  /** The overlay button that puts the picture back at its own size. */
  resetZoomLabel: string;
  /**
   * The hand(s) each circle is taken with, by its place in the editor's holds —
   * marked on that side of its ring (see `HandMark`). Asked of the page
   * rather than read off the circles: the hands follow from the order and are
   * worked out again on every change (`assignPhotoHands`), and a circle just
   * drawn or moved has none of its own until they are. Nothing on a climb that
   * forces none.
   */
  handsOf?: (index: number) => BoardHand[];
  /**
   * Turn the lights down on the wall and leave them on over the chosen holds.
   *
   * **The picture stops being a backdrop and becomes the answer.** On a scanned
   * wall every hold is already found, so the work is not drawing circles but
   * saying which plastic is this climb — and a dark wall with the chosen holds
   * lit says that at a glance, from across the room, in a way a ring around a
   * hold on a busy photograph never did.
   *
   * **Darkened rather than drained of colour**, which is what this was first
   * and what did not work: a grey wall with colour inside a few holds reads as
   * two pictures badly joined, and on a wall of pale plastic the holds that
   * were *in* were the hard ones to see. Turning the lights down instead keeps
   * one picture and one light in it, and the holds that are in are simply the
   * lit ones. Black and white stayed, but as what it is good at — one of the
   * effects a *reader* can ask a finished climb for (see
   * `climb-photo-effect.ts`), not how one is chosen.
   *
   * Off by default, so the member app's editor and every climb drawn by hand
   * are untouched.
   */
  spotlight?: boolean;
  /**
   * Which holds are lit, by index. Everything else is drawn as a **placeholder**
   * — there on the wall, pressable, but not part of anything yet.
   *
   * Left out, every hold is lit, which is what the climb editor wants: its
   * holds *are* the climb. The picker on a scan passes the selection instead,
   * because there the whole wall is on the photo and the climb is the handful
   * that have been pressed.
   */
  lit?: number[];
  /**
   * Which placeholders the detector was unsure of, by index. Drawn as
   * **maybes** while they are not lit — fainter, and dotted where a plain
   * placeholder is dashed — so the setter can tell "there, worth a tap" from
   * "perhaps a hold" at a glance. Lit, a maybe is a hold like any other.
   */
  maybe?: number[];
  /**
   * An area of the photo, in fractions of it, being looked at again for holds
   * the scan missed. Drawn over the wall until the answer is in, so the
   * setter can see what was asked and that it is still being worked on.
   */
  pendingArea?: { x: number; y: number; width: number; height: number };
  /**
   * **A press on the photo as a point**, instead of a gesture on the holds —
   * the scans panel's colour-wand. While set, the photo answers presses with
   * where they landed, in fractions of it, and nothing else hears them.
   */
  onPickPoint?: (point: { x: number; y: number }) => void;
  /**
   * A shape being proposed, in fractions of the photo — the wand's patch —
   * with the point it was grown from. Drawn over everything until it is
   * taken or dropped.
   */
  pickPreview?: { outline: Array<[number, number]> | null; point: { x: number; y: number } | null };
  /** Test hook for the frame, which is what presses are measured against. */
  testId?: string;
  className?: string;
}

/**
 * The photo with its circles: the surface a climb is drawn on.
 *
 * The moving layer is exactly the photo's box — the <img> sizes it and keeps its
 * own proportions at any width — so a fraction of the photo is a percentage of
 * that element and the circles need no projection of their own. Widening the
 * column changes the photo's height with its width, and every mark on it
 * follows. The frame around it is the same box, unless `className` gives it a
 * height of its own; the picture then covers it (see `useZoomPan`).
 *
 * The picture and the circles sit in one transformed layer, so zooming moves
 * both and they cannot drift apart. Presses are hit-tested by the frame (see
 * usePhotoHolds), which is what lets the smallest circle of a stack win.
 *
 * A hold that knows its own shape is the same button, sized to the shape's box
 * and **clipped to the shape** — which the browser honours when hit-testing, so
 * the tap target is the hold rather than the box around it, exactly as
 * `rounded-full` does that job for a circle. Its visible stroke is drawn in the
 * SVG layer below instead of as a border, because a clipped border has nothing
 * left to draw.
 */
export function PhotoHoldCanvas({
  editor,
  imageUrl,
  holdLabel,
  resetZoomLabel,
  handsOf,
  spotlight = false,
  lit,
  maybe,
  pendingArea,
  onPickPoint,
  pickPreview,
  testId,
  className,
}: PhotoHoldCanvasProps) {
  // One clip per instance on the page: two editors side by side must not light
  // each other's holds.
  const clipId = `${useId()}-lit`;
  const {
    zoom,
    holds,
    naturalWidth,
    naturalHeight,
    selected,
    selectedHold,
    resizable,
    stepNumbers,
    toggleSelected,
    band,
    frameProps,
  } = editor;

  // The holds with a shape of their own, which are the ones the SVG draws. On
  // every climb drawn by hand this is empty and the layer is not rendered at all.
  const shaped = holds
    .map((hold, index) => ({ hold, index, clipPath: holdClipPath(hold) }))
    .filter((entry) => entry.clipPath !== undefined);
  // The wall's untaken holds. Drawn under everything and never lit: they are
  // there to be pressed, not to be read as part of the climb.
  const candidates = editor.candidates ?? [];
  // A shape's line is a share of the hold rather than of the photo — see
  // `holdStrokeWidth`. Only shapes reach the SVG at all; a circle wears a CSS
  // border in screen pixels and is unaffected.
  const longEdge = Math.max(naturalWidth, naturalHeight);
  const strokeOf = (hold: Hold) => holdStrokeWidth(hold, naturalWidth, naturalHeight, longEdge);
  // Lit, or a placeholder waiting to be. With no list given every hold is lit:
  // the climb editor's holds are the climb.
  const isLit = (index: number) => lit === undefined || lit.includes(index);
  const isMaybe = (index: number) => !isLit(index) && (maybe?.includes(index) ?? false);

  return (
    <div
      {...zoom.frameProps}
      // The editor's gestures run after the zoom's, which only records pointers
      // for the pinch: with one-finger panning off there is nothing for them to
      // disagree about.
      onPointerDown={(event) => {
        zoom.frameProps.onPointerDown(event);
        frameProps.onPointerDown(event);
      }}
      onPointerMove={(event) => {
        zoom.frameProps.onPointerMove(event);
        frameProps.onPointerMove(event);
      }}
      onPointerUp={(event) => {
        zoom.frameProps.onPointerUp(event);
        frameProps.onPointerUp(event);
      }}
      onPointerCancel={(event) => {
        zoom.frameProps.onPointerCancel(event);
        frameProps.onPointerCancel(event);
      }}
      data-testid={testId}
      className={`relative w-full cursor-crosshair select-none overflow-hidden bg-muted ${className ?? ''}`}
    >
      <div ref={zoom.contentRef} {...zoom.layerProps}>
        <img
          src={imageUrl}
          alt=""
          className="block w-full"
          style={spotlight ? { filter: `brightness(${SPOTLIGHT_DIM})` } : undefined}
          draggable={false}
        />

        {/* The holds that are in, under the light.
            The same photograph again, clipped to the union of their shapes and
            left at its own brightness over a wall that has been turned down —
            which is what reads as a hold switched on rather than merely
            circled. It is the identical URL, so the browser has it decoded
            already and this costs a paint rather than a download. Under the
            strokes, so a ring is never covered by it. */}
        {spotlight && holds.length > 0 && naturalWidth > 0 && naturalHeight > 0 && (
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            viewBox={`0 0 ${naturalWidth} ${naturalHeight}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <clipPath id={clipId}>
                {holds.map((hold, index) =>
                  isLit(index) ? (
                    <path key={index} d={holdPath(hold, naturalWidth, naturalHeight)} />
                  ) : null
                )}
              </clipPath>
            </defs>
            <image
              href={imageUrl}
              x={0}
              y={0}
              width={naturalWidth}
              height={naturalHeight}
              preserveAspectRatio="none"
              clipPath={`url(#${clipId})`}
              style={{ filter: `brightness(${SPOTLIGHT_LIT}) saturate(1.15)` }}
            />
          </svg>
        )}

        {/* The holds the scan found that this climb has not taken. Dashed and
            faint: visible enough to press, quiet enough not to be mistaken for
            the climb. */}
        {candidates.length > 0 && naturalWidth > 0 && naturalHeight > 0 && (
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            viewBox={`0 0 ${naturalWidth} ${naturalHeight}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {candidates.map((hold, index) => (
              <path
                key={index}
                data-candidate=""
                d={holdPath(hold, naturalWidth, naturalHeight)}
                fill="none"
                stroke="#ffffff"
                strokeOpacity={0.5}
                strokeDasharray={`${strokeOf(hold) * 2} ${strokeOf(hold) * 2}`}
                strokeWidth={strokeOf(hold)}
              />
            ))}
          </svg>
        )}

        {/* The outlines of the holds that have one, and the halo that says which
            of them is picked up — both of which a circle wears as a CSS border
            and a ring, neither of which survives being clipped to a polygon.
            In the same transformed layer as the picture, and measured in its own
            pixels, so the stroke lands on the shape at any zoom. */}
        {shaped.length > 0 && naturalWidth > 0 && naturalHeight > 0 && (
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            viewBox={`0 0 ${naturalWidth} ${naturalHeight}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {shaped.map(({ hold, index }) => {
              // A placeholder is drawn as what it is: there on the wall, worth
              // pressing, and not part of anything yet. Faint and dashed rather
              // than wearing a role's colour, which it has not earned.
              if (!isLit(index)) {
                return (
                  <path
                    key={index}
                    data-hold=""
                    data-placeholder=""
                    data-maybe={isMaybe(index) ? '' : undefined}
                    d={holdPath(hold, naturalWidth, naturalHeight)}
                    fill="none"
                    stroke="#ffffff"
                    strokeOpacity={isMaybe(index) ? MAYBE_OPACITY : 0.55}
                    strokeDasharray={
                      isMaybe(index)
                        ? `${strokeOf(hold) * 0.6} ${strokeOf(hold) * 1.6}`
                        : `${strokeOf(hold) * 2} ${strokeOf(hold) * 2}`
                    }
                    strokeWidth={strokeOf(hold)}
                  />
                );
              }

              return (
                <path
                  key={index}
                  data-hold=""
                  d={holdPath(hold, naturalWidth, naturalHeight)}
                  fill="none"
                  stroke={selected.includes(index) ? '#ffffff' : hold.color}
                  strokeWidth={selected.includes(index) ? strokeOf(hold) * 2.5 : strokeOf(hold)}
                />
              );
            })}
            {/* The role's colour over the halo, so a picked-up shape reads as
                both at once — the white outside it and its own colour on it. */}
            {shaped
              .filter(({ index }) => selected.includes(index))
              .map(({ hold, index }) => (
                <path
                  key={`role-${index}`}
                  data-hold=""
                  d={holdPath(hold, naturalWidth, naturalHeight)}
                  fill="none"
                  stroke={hold.color}
                  strokeWidth={strokeOf(hold)}
                />
              ))}
          </svg>
        )}

        {/* The rectangle being swept, while it is being swept. In the same
            transformed layer as the picture, so it stays over the wall it is
            gathering from. */}
        {band && (
          <div
            data-testid="selection-band"
            className="pointer-events-none absolute border border-dashed border-white bg-white/15"
            style={{
              left: `${Math.min(band.x1, band.x2) * 100}%`,
              top: `${Math.min(band.y1, band.y2) * 100}%`,
              width: `${Math.abs(band.x2 - band.x1) * 100}%`,
              height: `${Math.abs(band.y2 - band.y1) * 100}%`,
            }}
          />
        )}

        {pickPreview && naturalWidth > 0 && naturalHeight > 0 && (
          <svg
            className="pointer-events-none absolute inset-0 z-10 h-full w-full"
            viewBox={`0 0 ${naturalWidth} ${naturalHeight}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            data-testid="pick-preview"
          >
            {pickPreview.outline && (
              <polygon
                points={pickPreview.outline
                  .map(([x, y]) => `${x * naturalWidth},${y * naturalHeight}`)
                  .join(' ')}
                fill="rgba(56, 189, 248, 0.35)"
                stroke="#38bdf8"
                strokeWidth={longEdge / 500}
                strokeLinejoin="round"
              />
            )}
            {pickPreview.point && (
              <circle
                cx={pickPreview.point.x * naturalWidth}
                cy={pickPreview.point.y * naturalHeight}
                r={longEdge / 250}
                fill="#ffffff"
                stroke="#0f172a"
                strokeWidth={longEdge / 1000}
              />
            )}
          </svg>
        )}

        {pendingArea && (
          <div
            data-testid="pending-area"
            className="pointer-events-none absolute animate-pulse rounded-sm border-2 border-dashed border-white bg-white/20 shadow-[0_0_0_1px_rgba(0,0,0,0.5)]"
            style={{
              left: `${pendingArea.x * 100}%`,
              top: `${pendingArea.y * 100}%`,
              width: `${pendingArea.width * 100}%`,
              height: `${pendingArea.height * 100}%`,
            }}
          />
        )}

        {holds.map((hold, index) => {
          // A hold that knows its own shape: the button becomes the shape's box
          // clipped to the shape, its ring is drawn in the SVG above, and the
          // marks it wears move out of the clip (below) so none of them is cut
          // in half by it.
          const clipPath = holdClipPath(hold);
          const box = {
            left: `${hold.x * 100}%`,
            top: `${hold.y * 100}%`,
            width: `${hold.width * 100}%`,
          };

          return (
          // A touch target, but not a click target.
          //
          // Which circle a press means is still the frame's to decide (see
          // usePhotoHolds), and that is what lets the smallest of a stack win —
          // this element never handles the press itself. It has to be hit-
          // testable all the same, because `touch-action` is only read off
          // whatever the finger actually landed on: a circle the browser cannot
          // see is a circle whose drag the browser hands to the page as a
          // scroll. `none` here is what keeps a hold under the finger that is
          // moving it.
          //
          // So the click is left to the keyboard, which is what the button was
          // always for — `detail === 0` is a press of Enter or Space rather than
          // a pointer, and a mouse click is already answered by the frame.
          <button
            key={index}
            type="button"
            aria-pressed={selected.includes(index)}
            aria-label={holdLabel({
              role: photoHoldRole(hold),
              x: Math.round(hold.x * 100),
              y: Math.round(hold.y * 100),
            })}
            onClick={(event) => {
              if (event.detail === 0) toggleSelected(index, event.metaKey || event.ctrlKey);
            }}
            data-hands={handsOf?.(index)?.join(',') || undefined}
            data-maybe={isMaybe(index) ? '' : undefined}
            className={`absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center border-[3px] ${
              clipPath ? '' : 'rounded-full'
            } ${isMaybe(index) && !clipPath ? 'border-dotted opacity-60' : ''} ${
              selected.includes(index) && !clipPath
                ? 'ring-2 ring-white ring-offset-1 ring-offset-black/40'
                : ''
            }`}
            style={{
              ...box,
              // A circle is as tall as it is wide; a shape is as tall as its own
              // box, which is what the clip-path's percentages are measured in.
              ...(clipPath
                ? { height: `${hold.height * 100}%`, clipPath }
                : { aspectRatio: '1 / 1' }),
              // The ring is the role; the hand that takes the hold, where one is
              // forced, is marked on its own side of that ring (HandMark below).
              // A shape's ring is the SVG's, since a clipped border would be
              // left drawing slivers of this box.
              borderColor: clipPath ? 'transparent' : hold.color,
              backgroundColor: 'transparent',
              touchAction: 'none',
            }}
          >
            {clipPath ? null : (
              <HoldMarks hands={handsOf?.(index) ?? []} step={stepNumbers[index]} />
            )}
          </button>
          );
        })}

        {/* What a shape wears, kept out of the clip: a number cut off at the
            edge of a polygon is worse than one that overlaps it. Not a press
            target — the button above is, and the frame decides anyway. */}
        {shaped.map(({ hold, index }) => (
          <span
            key={`marks-${index}`}
            aria-hidden
            className="pointer-events-none absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center"
            style={{
              left: `${hold.x * 100}%`,
              top: `${hold.y * 100}%`,
              width: `${hold.width * 100}%`,
              height: `${hold.height * 100}%`,
            }}
          >
            <HoldMarks hands={handsOf?.(index) ?? []} step={stepNumbers[index]} />
          </span>
        ))}

        {/* The selected circle's four handles. Which one a press means is the
            frame's to work out, like everything else on the photo; they are
            hit-testable only so a drag that starts on one is not read as the
            page being scrolled — see the circles above. Their size is divided
            back out of the zoom: a handle is there to be grabbed, and one that
            grew with the picture would cover the hold it is being aimed at.
            None on a mark whose size is not anybody's to change: four handles
            that do nothing when dragged are worse than none. */}
        {selectedHold &&
          resizable &&
          HOLD_HANDLES.map(({ dx, dy }) => (
            <span
              key={`${dx},${dy}`}
              aria-hidden
              data-testid="photo-hold-handle"
              className="absolute size-2.5 rounded-sm border border-black/50 bg-white shadow"
              style={{
                left: `${(selectedHold.x + (dx * selectedHold.width) / 2) * 100}%`,
                top: `${(selectedHold.y + (dy * selectedHold.height) / 2) * 100}%`,
                transform: `translate(-50%, -50%) scale(${1 / zoom.scale})`,
                touchAction: 'none',
              }}
            />
          ))}
        {onPickPoint && (
          // Over everything, holds included, and swallowing the press so the
          // frame's own gestures never see it.
          <div
            data-testid="pick-surface"
            className="absolute inset-0 z-20 cursor-cell"
            onPointerDown={(event) => event.stopPropagation()}
            onPointerMove={(event) => event.stopPropagation()}
            onPointerUp={(event) => {
              event.stopPropagation();
              const box = event.currentTarget.getBoundingClientRect();
              if (box.width <= 0 || box.height <= 0) return;

              onPickPoint({
                x: Math.min(1, Math.max(0, (event.clientX - box.left) / box.width)),
                y: Math.min(1, Math.max(0, (event.clientY - box.top) / box.height)),
              });
            }}
          />
        )}
      </div>

      <ZoomResetButton zoom={zoom} label={resetZoomLabel} />
    </div>
  );
}

/**
 * What a mark wears over the hold itself: the hand it is taken with, where a
 * circuit forces one, and where it sits in that circuit.
 *
 * Inside the ring and in white with a shadow under it, which is the one colour
 * that reads on a photograph of any wall — the ring is already carrying the
 * role's colour. A foot wears no number: the run is stood on it throughout
 * rather than stopping there.
 *
 * Drawn inside a circle's own button, and in a layer of its own over a shape,
 * whose button is clipped to the shape and would cut these in half.
 */
function HoldMarks({ hands, step }: { hands: BoardHand[]; step?: string | null }) {
  return (
    <>
      {/* On a shape this ring reads round the shape's box rather than along its
          edge — enough to say which hand is meant, which is all it is for. */}
      <HandMark hands={hands} ring={PHOTO_RING_PX} />
      {step && (
        <span
          className={`whitespace-nowrap font-bold leading-none text-white ${
            step.length > 2 ? 'text-[8px]' : 'text-[10px]'
          }`}
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}
        >
          {step}
        </span>
      )}
    </>
  );
}
